import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BusinessErrorService } from '../../foundry-shared/business-error.service';

// ─── Types ────────────────────────────────────────────────────────────────────
export type AttrType     = 'string' | 'integer' | 'long' | 'numeric' | 'datetime' | 'boolean' | 'array' | 'json';
export type EntityTypeVal = 'NonTimeSeries' | 'timeSeries';

export type StatusOperator =
  'NOT_NULL' | 'NOT_EMPTY' | 'EQUAL' | 'NOT_EQUAL' |
  'GREATER_THAN' | 'LESS_THAN' | 'GREATER_THAN_EQUAL' | 'LESS_THAN_EQUAL';

export interface StatusValidationRule {
  id: string;
  operator: StatusOperator;
  attributeName: string;
  value: string;
  logicalOperator: 'AND' | 'OR' | '';
}

export interface StatusStateConfig {
  targetAttribute: string;
  workflow: string;
  nextStatus: string;
  onFail: string;
  validation: StatusValidationRule[];
}

export interface StatusManagement {
  type: string;
  statusValidation: Record<string, StatusStateConfig>;
}

export interface DeamAttr {
  id: string;
  name: string;
  displayName: string;
  type: AttrType;
  subType: string;
  // Constraints
  mandatory: boolean;
  unique: boolean;
  indexed: boolean;
  businessKey: boolean;
  autoGenerated: boolean;
  enableAttribute: boolean;
  enableUpdate: boolean;
  allowNull: boolean;
  versioned: boolean;
  // Type-specific
  maxLength: number | null;
  precision: number | null;
  scale: number | null;
  dateformat: string;
  staticString: string;
  validValues: string[];
  compositeUnique: string[];
  // FK
  referencedEntity: string | null;
  referencedAttribute: string | null;
  // Status state machine
  statusManagement: StatusManagement | null;
  // UI
  expanded: boolean;
}

// DEAM Reads — a single unified read concept in the UI (no separate
// static/dynamic authoring). Filter conditions are built the same way as an
// approval workflow's "Trigger Conditions" builder (pick an entity, then
// field/operator/value rows chained with AND/OR). On export, a read with no
// conditions becomes a staticDeamRead (plain "SELECT * FROM entity"); a read
// with 1+ conditions becomes a dynamicDeamRead with a compiled @attr@-style
// SQL "where" fragment — matching the real backend's two-map contract.
export type TriggerOperator = '>' | '<' | '>=' | '<=' | '==' | '!=' | 'between';

export interface DynamicReadFilter {
  id: string;
  attributeName: string;
  operator: TriggerOperator;
  value: string;
  value2: string; // only used when operator === 'between'
  logicalOperator: string; // '', 'AND', 'OR'
  scope: string; // matches the real backend filter's "scope" field, e.g. a grouping/paren scope
}

export interface DeamRead {
  id: string;
  name: string;
  entityName: string; // drives which entity's attributes populate the Field dropdown
  filters: DynamicReadFilter[];
}

// Business rules — matches intraEntityRules/interEntityRules/globalEntityRules + operation/transformation
export interface BizRule {
  id: string;
  attributeName: string;
  operator: string; // top-level combinator, e.g. "AND"
  filterOperator: StatusOperator;
  filterValue: string;
  logicalOperator: 'AND' | 'OR' | '';
  subOperator: string;
}

export interface Transformation {
  assignedValueType: 'value' | 'attribute';
  value: string;
}

export interface BizRules {
  intraEntityRulesEnabled: boolean;
  interEntityRulesEnabled: boolean;
  globalEntityRulesEnabled: boolean;
  intraEntityRules: BizRule[];
  interEntityRules: BizRule[];
  globalEntityRules: BizRule[];
  operation: string;
  transformation: Record<string, Transformation>;
}

// Child entities — reference to another top-level entity plus the attribute on
// that child which links back to this (parent) entity, matching the real
// backend's childEntities[]/parentEntities{} pairing.
export interface ChildEntityLink {
  id: string;
  entityName: string;
  linkAttribute: string;
}

// A node in the entity-relationship hierarchy view (traverses childEntities
// recursively starting from the selected entity). `isCircular` marks a node
// whose entityName already appears in an ancestor of itself — traversal stops
// there instead of recursing forever.
export interface HierarchyNode {
  entityName: string;
  linkAttribute: string; // '' for the root node
  isCircular: boolean;
  missing: boolean; // referenced entity no longer exists in the workspace
  children: HierarchyNode[];
}

export interface RolePermission {
  id: string;
  roleName: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canAdmin: boolean;
}

export interface DeamEntity {
  id: string;
  entity: string;
  displayName: string;
  description: string;
  category: string;
  entityType: EntityTypeVal;
  primaryKey: string;
  primaryKeyType: string;
  primaryKeyAutogenerated: boolean;
  dataVersionEnabled: boolean;
  versioningType: string;
  maxVersion: number;
  isBusinessEntity: boolean;
  dataObjectKey: string;
  isEnabled: boolean;
  attributesList: DeamAttr[];
  deamReads: DeamRead[];
  childEntities: ChildEntityLink[];
  businessRules: BizRules;
  permissions: RolePermission[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function mkAttr(overrides: Partial<DeamAttr> & { id: string; name: string; type: AttrType }): DeamAttr {
  return {
    displayName: '', subType: '', mandatory: false, unique: false, indexed: false,
    businessKey: false, autoGenerated: false, enableAttribute: true, enableUpdate: true,
    allowNull: true, versioned: false, maxLength: null, precision: null, scale: null,
    dateformat: '', staticString: '', validValues: [], compositeUnique: [],
    referencedEntity: null, referencedAttribute: null, statusManagement: null, expanded: false,
    ...overrides,
  };
}

function emptyBizRules(): BizRules {
  return {
    intraEntityRulesEnabled: false, interEntityRulesEnabled: false, globalEntityRulesEnabled: false,
    intraEntityRules: [], interEntityRules: [], globalEntityRules: [],
    operation: '', transformation: {},
  };
}

// ─── Sample data ──────────────────────────────────────────────────────────────
@Component({
  selector: 'app-entity-detail-accordion',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule],
  templateUrl: './entity-detail-accordion.component.html',
  styleUrls: ['./entity-detail-accordion.component.scss'],
})
export class EntityDetailAccordionComponent implements OnChanges, OnDestroy {
  @Input() entities: DeamEntity[] = [];
  @Output() entitiesChange = new EventEmitter<{ entities: DeamEntity[]; selectedId: string | null }>();
  @Output() toast = new EventEmitter<string>();
  @Output() saveEntity = new EventEmitter<DeamEntity>();
  @Output() sidebarCollapsedChange = new EventEmitter<boolean>();
  @Input() selectedId: string | null = null;

  // Panel open states
  panels: Record<string, boolean> = { info: false, attrs: false, reads: false, rules: false, children: false, perms: false };

  // UI flags
  showAddModal    = false;
  editingAttr: DeamAttr | null = null;
  draftAttr: DeamAttr | null = null;
  @Input() isSidebarCollapsed = false;
  showJsonPanel   = false;
  showHierarchyPanel = false;
  showSearchDrop  = false;
  attrSearch      = '';
  entitySearch    = '';
  @Input() saveStatus: 'idle' | 'saving' | 'saved' = 'idle';

  // New entity modal
  newEnt = { entity: '', displayName: '', entityType: 'NonTimeSeries' as EntityTypeVal, isBusinessEntity: true, dataVersionEnabled: false };

  // Add DEAM read buffer
  newRead = { name: '' };
  expandedReadId: string | null = null;
  readonly TRIGGER_OPERATORS: TriggerOperator[] = ['>', '<', '>=', '<=', '==', '!=', 'between'];
  private readonly OP_SQL: Record<TriggerOperator, string> = { '>': '>', '<': '<', '>=': '>=', '<=': '<=', '==': '=', '!=': '!=', between: 'BETWEEN' };

  // Add rule buffers
  newRuleMap: Record<string, { attributeName: string; filterOperator: StatusOperator; filterValue: string; logicalOperator: 'AND' | 'OR' | '' }> = {
    intra:  { attributeName: '', filterOperator: 'NOT_NULL', filterValue: '', logicalOperator: '' },
    inter:  { attributeName: '', filterOperator: 'NOT_NULL', filterValue: '', logicalOperator: '' },
    global: { attributeName: '', filterOperator: 'NOT_NULL', filterValue: '', logicalOperator: '' },
  };
  newTransformAttr = '';

  // Add child entity link
  newChildEntityLink = { entityName: '', linkAttribute: '' };

  // Add permission
  newPermRole = '';

  // Valid values per attr
  newValidValueMap: Record<string, string> = {};

  readonly ATTR_TYPES: AttrType[]  = ['string', 'integer', 'long', 'numeric', 'datetime', 'boolean', 'array', 'json'];
  readonly SUB_TYPES               = ['string', 'integer', 'long', 'numeric', 'datetime', 'boolean', 'json'];
  readonly STATUS_OPERATORS: StatusOperator[] = [
    'NOT_NULL', 'NOT_EMPTY', 'EQUAL', 'NOT_EQUAL',
    'GREATER_THAN', 'LESS_THAN', 'GREATER_THAN_EQUAL', 'LESS_THAN_EQUAL',
  ];
  readonly VERSIONING_TYPES        = ['timebased', 'snapshot', 'delta'];
  readonly PK_TYPES                = ['string', 'integer', 'long'];
  readonly TRANSFORM_VALUE_TYPES: Array<Transformation['assignedValueType']> = ['value', 'attribute'];

  get entityRegistry(): string[] { return this.entities.map(entity => entity.entity); }

  private destroy$ = new Subject<void>();

  constructor(private businessError: BusinessErrorService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedId']) {
      this.panels = { info: false, attrs: false, reads: false, rules: false, children: false, perms: false };
      this.attrSearch = '';
      this.showJsonPanel = false;
      this.showHierarchyPanel = false;
      this.editingAttr = null;
      this.draftAttr = null;
    }
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  // ── Selection ────────────────────────────────────────────────────────────────
  get selected(): DeamEntity | undefined { return this.entities.find(e => e.id === this.selectedId); }

  selectEntity(id: string): void {
    if (this.selectedId === id) return;
    this.selectedId = id;
    this.panels = { info: false, attrs: false, reads: false, rules: false, children: false, perms: false };
    this.attrSearch = '';
    this.showJsonPanel = false;
    this.showHierarchyPanel = false;
  }

  // Accordion: opening a panel closes every other one.
  togglePanel(name: string): void {
    const isOpen = this.panels[name];
    for (const key of Object.keys(this.panels)) {
      this.panels[key] = false;
    }
    this.panels[name] = !isOpen;
  }

  // ── Persistence ───────────────────────────────────────────────────────────────
  persist(): void {
    this.entitiesChange.emit({ entities: this.entities, selectedId: this.selectedId });
  }

  // ── Filtered entity list ──────────────────────────────────────────────────────
  get filteredEntities(): DeamEntity[] {
    const q = this.entitySearch.toLowerCase();
    return q ? this.entities.filter(e => e.entity.toLowerCase().includes(q) || e.displayName.toLowerCase().includes(q)) : this.entities;
  }

  // ── Filtered attribute list ───────────────────────────────────────────────────
  get filteredAttrs(): DeamAttr[] {
    if (!this.selected) return [];
    const q = this.attrSearch.toLowerCase();
    return q ? this.selected.attributesList.filter(a => a.name.toLowerCase().includes(q) || a.displayName.toLowerCase().includes(q)) : this.selected.attributesList;
  }

  // ── Attribute CRUD ────────────────────────────────────────────────────────────
  openAddAttrModal(): void {
    this.draftAttr = mkAttr({ id: '__draft__', name: '', type: 'string', maxLength: 255, expanded: true });
  }

  closeAddAttrModal(): void { this.draftAttr = null; }

  addAttribute(): void {
    const ent = this.selected;
    const draft = this.draftAttr;
    if (!ent || !draft || !draft.name.trim()) return;
    if (ent.attributesList.some(a => a.name === draft.name.trim())) {
      this.showToast(`Attribute "${draft.name.trim()}" already exists`); return;
    }
    const attr: DeamAttr = { ...draft, id: `attr_${Date.now()}`, name: draft.name.trim() };
    ent.attributesList = [...ent.attributesList, attr];
    this.draftAttr = null;
    this.persist();
    this.showToast(`Attribute "${attr.name}" added`);
  }

  removeAttribute(attrId: string): void {
    if (!this.selected) return;
    if (this.editingAttr?.id === attrId) this.editingAttr = null;
    this.selected.attributesList = this.selected.attributesList.filter(a => a.id !== attrId);
    this.persist();
    this.showToast('Attribute removed');
  }

  toggleAttrExpand(attr: DeamAttr): void { attr.expanded = !attr.expanded; }

  // ── Attribute config modal ──────────────────────────────────────────────────
  openAttrModal(attr: DeamAttr): void { this.editingAttr = attr; }

  closeAttrModal(): void { this.editingAttr = null; this.persist(); }

  // ── Status validation (state-machine config) ────────────────────────────────
  private emptyStatusState(): StatusStateConfig {
    return { targetAttribute: '', workflow: '', nextStatus: '', onFail: '', validation: [] };
  }

  toggleStatusValidation(attr: DeamAttr): void {
    attr.statusManagement = attr.statusManagement
      ? null
      : { type: 'record', statusValidation: { DRAFT: this.emptyStatusState() } };
    this.persist();
  }

  stateNames(attr: DeamAttr): string[] {
    return attr.statusManagement ? Object.keys(attr.statusManagement.statusValidation) : [];
  }

  addState(attr: DeamAttr): void {
    if (!attr.statusManagement) return;
    const existing = this.stateNames(attr);
    let n = 1;
    let name = 'NEW_STATE';
    while (existing.includes(name)) { n++; name = `NEW_STATE_${n}`; }
    attr.statusManagement.statusValidation[name] = this.emptyStatusState();
    this.persist();
  }

  removeState(attr: DeamAttr, stateName: string): void {
    if (!attr.statusManagement) return;
    delete attr.statusManagement.statusValidation[stateName];
    this.persist();
  }

  renameState(attr: DeamAttr, oldName: string, newName: string): void {
    if (!attr.statusManagement) return;
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName || attr.statusManagement.statusValidation[trimmed]) return;
    const map = attr.statusManagement.statusValidation;
    const reordered: Record<string, StatusStateConfig> = {};
    for (const key of Object.keys(map)) reordered[key === oldName ? trimmed : key] = map[key];
    attr.statusManagement.statusValidation = reordered;
    this.persist();
  }

  addValidationRule(attr: DeamAttr, stateName: string): void {
    const state = attr.statusManagement?.statusValidation[stateName];
    if (!state) return;
    state.validation = [...state.validation, {
      id: `sr_${Date.now()}`, operator: 'NOT_NULL', attributeName: '', value: '', logicalOperator: 'AND',
    }];
    this.persist();
  }

  removeValidationRule(attr: DeamAttr, stateName: string, ruleId: string): void {
    const state = attr.statusManagement?.statusValidation[stateName];
    if (!state) return;
    state.validation = state.validation.filter(r => r.id !== ruleId);
    this.persist();
  }

  // ── FK reference dropdowns ──────────────────────────────────────────────────
  get referencableEntities(): string[] {
    return this.entities.map(e => e.entity);
  }

  referencableAttributes(entityName: string | null): string[] {
    if (!entityName) return [];
    const ent = this.entities.find(e => e.entity === entityName);
    return ent ? ent.attributesList.map(a => a.name) : [];
  }

  onReferencedEntityChange(attr: DeamAttr, entityName: string | null): void {
    attr.referencedEntity = entityName;
    attr.referencedAttribute = null;
    this.persist();
  }

  dropAttr(event: CdkDragDrop<DeamAttr[]>): void {
    if (!this.selected || event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.selected.attributesList, event.previousIndex, event.currentIndex);
    this.persist();
  }

  addValidValue(attr: DeamAttr): void {
    const val = (this.newValidValueMap[attr.id] || '').trim();
    if (!val) return;
    attr.validValues = [...attr.validValues, val];
    this.newValidValueMap[attr.id] = '';
    this.persist();
  }

  removeValidValue(attr: DeamAttr, idx: number): void {
    attr.validValues = attr.validValues.filter((_, i) => i !== idx);
    this.persist();
  }

  // ── DEAM Reads ────────────────────────────────────────────────────────────────
  addRead(): void {
    const ent = this.selected;
    if (!ent || !this.newRead.name.trim()) return;
    const r: DeamRead = {
      id: `read_${Date.now()}`,
      name: this.newRead.name.trim(),
      entityName: ent.entity,
      filters: [],
    };
    ent.deamReads = [...ent.deamReads, r];
    this.expandedReadId = r.id;
    this.newRead = { name: '' };
    this.persist();
  }

  removeRead(readId: string): void {
    if (!this.selected) return;
    this.selected.deamReads = this.selected.deamReads.filter(r => r.id !== readId);
    if (this.expandedReadId === readId) this.expandedReadId = null;
    this.persist();
  }

  toggleReadExpand(readId: string): void {
    this.expandedReadId = this.expandedReadId === readId ? null : readId;
  }

  // Trigger-condition-style filter builder (same pattern as an approval
  // workflow's TRIGGER CONDITIONS panel: chain field/operator/value rows
  // with AND/OR). Entity is fixed to the read's owning entity.

  addDynFilter(read: DeamRead): void {
    const firstAttr = this.childEntityAttributes(read.entityName)[0] || '';
    read.filters = [...read.filters, {
      id: `drf_${Date.now()}`,
      attributeName: firstAttr,
      operator: '==',
      value: '',
      value2: '',
      logicalOperator: read.filters.length ? 'AND' : '',
      scope: '',
    }];
    this.persist();
  }

  removeDynFilter(read: DeamRead, filterId: string): void {
    read.filters = read.filters.filter(f => f.id !== filterId);
    this.persist();
  }

  updateDynFilterLogic(filter: DynamicReadFilter, logic: 'AND' | 'OR'): void {
    filter.logicalOperator = logic;
    this.persist();
  }

  buildFilterWhere(f: DynamicReadFilter): string {
    if (f.operator === 'between') {
      const lo = f.value.trim() || `@${f.attributeName}_min@`;
      const hi = f.value2.trim() || `@${f.attributeName}_max@`;
      return `${f.attributeName} BETWEEN ${lo} AND ${hi}`;
    }
    const val = f.value.trim() || `@${f.attributeName}@`;
    return `${f.attributeName} ${this.OP_SQL[f.operator]} ${val}`;
  }

  // ── Business Rules ────────────────────────────────────────────────────────────
  private ruleListKey(scope: 'intra' | 'inter' | 'global'): 'intraEntityRules' | 'interEntityRules' | 'globalEntityRules' {
    return scope === 'intra' ? 'intraEntityRules' : scope === 'inter' ? 'interEntityRules' : 'globalEntityRules';
  }

  addRule(scope: 'intra' | 'inter' | 'global'): void {
    const ent = this.selected;
    const buf = this.newRuleMap[scope];
    if (!ent || !buf.attributeName.trim()) return;
    const key = this.ruleListKey(scope);
    const rule: BizRule = {
      id: `br_${Date.now()}`,
      attributeName: buf.attributeName.trim(),
      operator: 'AND',
      filterOperator: buf.filterOperator,
      filterValue: buf.filterValue,
      logicalOperator: ent.businessRules[key].length ? buf.logicalOperator : '',
      subOperator: '',
    };
    ent.businessRules[key] = [...ent.businessRules[key], rule];
    this.newRuleMap[scope] = { attributeName: '', filterOperator: 'NOT_NULL', filterValue: '', logicalOperator: 'AND' };
    this.persist();
  }

  removeRule(scope: 'intra' | 'inter' | 'global', ruleId: string): void {
    const ent = this.selected;
    if (!ent) return;
    const key = this.ruleListKey(scope);
    ent.businessRules[key] = ent.businessRules[key].filter(r => r.id !== ruleId);
    this.persist();
  }

  // Entity-level transformation map (attributeName -> assigned value)
  addTransformation(): void {
    const ent = this.selected;
    const attr = this.newTransformAttr.trim();
    if (!ent || !attr) return;
    ent.businessRules.transformation = {
      ...ent.businessRules.transformation,
      [attr]: { assignedValueType: 'value', value: '' },
    };
    this.newTransformAttr = '';
    this.persist();
  }

  removeTransformation(attributeName: string): void {
    const ent = this.selected;
    if (!ent) return;
    const { [attributeName]: _removed, ...rest } = ent.businessRules.transformation;
    ent.businessRules.transformation = rest;
    this.persist();
  }

  transformationKeys(ent: DeamEntity): string[] {
    return Object.keys(ent.businessRules.transformation);
  }

  // ── Child entities ────────────────────────────────────────────────────────────
  childEntitySummary(ent: DeamEntity): string {
    return ent.childEntities.map(c => c.entityName).join(', ');
  }

  childEntityOptions(excludeName: string): string[] {
    return this.entities.map(e => e.entity).filter(n => n !== excludeName);
  }

  childEntityAttributes(entityName: string): string[] {
    const ent = this.entities.find(e => e.entity === entityName);
    return ent ? ent.attributesList.map(a => a.name) : [];
  }

  addChildEntity(): void {
    const ent = this.selected;
    if (!ent || !this.newChildEntityLink.entityName.trim() || !this.newChildEntityLink.linkAttribute.trim()) return;
    if (ent.childEntities.some(c => c.entityName === this.newChildEntityLink.entityName.trim())) {
      this.showToast(`"${this.newChildEntityLink.entityName}" is already a child entity`);
      return;
    }
    ent.childEntities = [...ent.childEntities, {
      id: `ce_${Date.now()}`,
      entityName: this.newChildEntityLink.entityName.trim(),
      linkAttribute: this.newChildEntityLink.linkAttribute.trim(),
    }];
    this.newChildEntityLink = { entityName: '', linkAttribute: '' };
    this.persist();
  }

  removeChildEntity(linkId: string): void {
    if (!this.selected) return;
    this.selected.childEntities = this.selected.childEntities.filter(c => c.id !== linkId);
    this.persist();
  }

  // ── Relationship hierarchy (child-entity connection graph) ──────────────────────
  toggleJsonPanel(): void {
    this.showJsonPanel = !this.showJsonPanel;
    if (this.showJsonPanel) this.showHierarchyPanel = false;
  }

  toggleHierarchyPanel(): void {
    this.showHierarchyPanel = !this.showHierarchyPanel;
    if (this.showHierarchyPanel) this.showJsonPanel = false;
  }

  private buildHierarchyNode(entityName: string, linkAttribute: string, path: string[]): HierarchyNode {
    const ent = this.entities.find(e => e.entity === entityName);
    const isCircular = path.includes(entityName);
    const missing = !ent;
    const node: HierarchyNode = { entityName, linkAttribute, isCircular, missing, children: [] };
    if (!ent || isCircular) return node;
    const nextPath = [...path, entityName];
    node.children = ent.childEntities.map(c => this.buildHierarchyNode(c.entityName, c.linkAttribute, nextPath));
    return node;
  }

  get hierarchyRoot(): HierarchyNode | null {
    const ent = this.selected;
    if (!ent) return null;
    return this.buildHierarchyNode(ent.entity, '', []);
  }

  get hierarchyHasCircular(): boolean {
    const hasCircular = (node: HierarchyNode): boolean => node.isCircular || node.children.some(hasCircular);
    const root = this.hierarchyRoot;
    return root ? hasCircular(root) : false;
  }

  // ── Permissions ───────────────────────────────────────────────────────────────
  addPermission(): void {
    const ent = this.selected;
    if (!ent || !this.newPermRole.trim()) return;
    if (!ent.permissions.some(p => p.roleName === this.newPermRole.trim())) {
      ent.permissions = [...ent.permissions, { id: `perm_${Date.now()}`, roleName: this.newPermRole.trim(), canRead: true, canWrite: false, canDelete: false, canAdmin: false }];
      this.persist();
    }
    this.newPermRole = '';
  }

  removePermission(idx: number): void {
    if (!this.selected) return;
    this.selected.permissions.splice(idx, 1);
    this.persist();
  }

  // ── Entity CRUD ───────────────────────────────────────────────────────────────
  openAddModal(): void {
    this.showAddModal = true;
    this.newEnt = { entity: '', displayName: '', entityType: 'NonTimeSeries', isBusinessEntity: true, dataVersionEnabled: false };
  }

  closeAddModal(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) this.showAddModal = false;
  }

  confirmAddEntity(): void {
    if (!this.newEnt.entity.trim()) return;
    const ent: DeamEntity = {
      id: `ent_${Date.now()}`,
      entity: this.newEnt.entity.trim(),
      displayName: this.newEnt.displayName,
      description: '', category: '',
      entityType: this.newEnt.entityType,
      primaryKey: 'uuid', primaryKeyType: 'string', primaryKeyAutogenerated: false,
      dataVersionEnabled: this.newEnt.dataVersionEnabled, versioningType: 'timebased', maxVersion: 10,
      isBusinessEntity: this.newEnt.isBusinessEntity, dataObjectKey: '', isEnabled: true,
      attributesList: [], deamReads: [], childEntities: [],
      businessRules: emptyBizRules(),
      permissions: [{ id: `perm_${Date.now()}`, roleName: 'Admin', canRead: true, canWrite: true, canDelete: true, canAdmin: true }],
    };
    this.entities = [...this.entities, ent];
    this.selectedId = ent.id;
    this.showAddModal = false;
    this.persist();
    this.showToast(`Entity "${ent.entity}" created`);
  }

  deleteEntity(id: string): void {
    this.entities = this.entities.filter(e => e.id !== id);
    if (this.selectedId === id) this.selectedId = this.entities[0]?.id ?? null;
    this.persist();
    this.showToast('Entity deleted');
  }

  loadFromRegistry(name: string): void {
    this.showSearchDrop = false;
    this.entitySearch = '';
    if (this.entities.some(e => e.entity === name)) { this.showToast(`"${name}" already exists`); return; }
    const ent: DeamEntity = {
      id: `ent_${Date.now()}`, entity: name, displayName: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      description: '', category: '',
      entityType: 'NonTimeSeries', primaryKey: 'uuid', primaryKeyType: 'string', primaryKeyAutogenerated: false,
      dataVersionEnabled: false, versioningType: '', maxVersion: 0, isBusinessEntity: true, dataObjectKey: '', isEnabled: false,
      attributesList: [], deamReads: [], childEntities: [],
      businessRules: emptyBizRules(),
      permissions: [{ id: `perm_${Date.now()}`, roleName: 'Admin', canRead: true, canWrite: true, canDelete: true, canAdmin: true }],
    };
    this.entities = [...this.entities, ent];
    this.selectedId = ent.id;
    this.persist();
    this.showToast(`"${name}" loaded from registry`);
  }

  get filteredRegistry(): string[] {
    const q = this.entitySearch.toLowerCase();
    return this.entityRegistry.filter(n => n.toLowerCase().includes(q));
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!(e.target as HTMLElement).closest('.ent-search-wrap')) this.showSearchDrop = false;
  }

  // ── Actions ───────────────────────────────────────────────────────────────────
  save(): void {
    if (this.saveStatus !== 'saving' && this.selected) this.saveEntity.emit(this.selected);
  }

  validate(): void {
    const ent = this.selected;
    if (!ent) return;
    const issues: string[] = [];
    if (!ent.entity) issues.push('entity name is required');
    if (!ent.attributesList.length) issues.push('at least one attribute required');
    const noKey = !ent.attributesList.some(a => a.businessKey);
    if (noKey && ent.isBusinessEntity) issues.push('no business key defined');
    if (issues.length) {
      this.businessError.show('ENTITY_VALIDATION_FAILED', issues[0]);
    } else {
      this.showToast('✓ Entity configuration is valid');
    }
  }

  goBack(): void { window.history.back(); }

  // ── JSON export ───────────────────────────────────────────────────────────────
  private buildRuleExport(rules: BizRule[]) {
    return rules.map(r => ({
      attributeName: r.attributeName,
      operator: r.operator,
      filter: { [r.filterOperator]: r.filterValue },
      logicalOperator: r.logicalOperator,
      subOperator: r.subOperator,
      subBusinessRules: null,
    }));
  }

  private buildEntityExport(ent: DeamEntity, parentEntities: Record<string, string> = {}, visited: Set<string> = new Set()): any {
    if (visited.has(ent.entity)) return null;
    visited = new Set(visited).add(ent.entity);
    return {
      entityName: ent.entity,
      displayName: ent.displayName,
      description: ent.description,
      category: ent.category,
      type: ent.isBusinessEntity ? 'businessEntity' : 'systemEntity',
      businessEntity: ent.isBusinessEntity,
      primaryKey: ent.primaryKey,
      dataVersioned: ent.dataVersionEnabled,
      versioningType: ent.dataVersionEnabled ? ent.versioningType : undefined,
      maxVersion: ent.dataVersionEnabled ? ent.maxVersion : -1,
      attributes: Object.fromEntries(ent.attributesList.map(({ id, expanded, ...a }) => [a.name, a])),
      dynamicDeamReads: Object.fromEntries(
        ent.deamReads.filter(r => r.filters.length).map(r => [r.name, {
          query: `SELECT * FROM ${r.entityName} where @where@`,
          filters: Object.fromEntries(r.filters.map(f => [f.attributeName, {
            logicalOperator: f.logicalOperator, scope: f.scope, where: this.buildFilterWhere(f), attributeName: f.attributeName,
          }])),
        }])
      ),
      staticDeamReads: Object.fromEntries(
        ent.deamReads.filter(r => !r.filters.length).map(r => [r.name, `SELECT * FROM ${r.entityName}`])
      ),
      businessRules: {
        intraEntityRulesEnabled: ent.businessRules.intraEntityRulesEnabled,
        interEntityRulesEnabled: ent.businessRules.interEntityRulesEnabled,
        globalEntityRulesEnabled: ent.businessRules.globalEntityRulesEnabled,
        intraEntityRules: this.buildRuleExport(ent.businessRules.intraEntityRules),
        interEntityRules: this.buildRuleExport(ent.businessRules.interEntityRules),
        globalEntityRules: this.buildRuleExport(ent.businessRules.globalEntityRules),
        operation: ent.businessRules.operation || null,
        transformation: ent.businessRules.transformation,
      },
      childEntities: ent.childEntities.map(link => {
        const child = this.entities.find(e => e.entity === link.entityName);
        return child ? this.buildEntityExport(child, { [link.linkAttribute]: ent.entity }, visited) : null;
      }).filter(Boolean),
      parentEntities,
    };
  }

  get selectedJson(): string {
    const ent = this.selected;
    if (!ent) return '';
    return JSON.stringify(this.buildEntityExport(ent), null, 2);
  }

  copyJson(): void {
    navigator.clipboard.writeText(this.selectedJson).then(() => this.showToast('JSON copied to clipboard'));
  }

  // ── Toast ─────────────────────────────────────────────────────────────────────
  private showToast(msg: string): void {
    this.toast.emit(msg);
  }
}
